import "./index.scss";

import { Box, MenuItem, Pagination, SelectChangeEvent, Stack } from "@mui/material";
import { ReactComponent as DropdownIcon } from "assets/images/icons/dropdown.svg";
import { ReactComponent as DropdownUpIcon } from "assets/images/icons/dropdown-up.svg";
import Button from "components/Button";
import DatePicker from "components/DatePicker";
import Dropdown from "components/Dropdown";
import { PAGE_OPTIONS } from "components/Pagination";
import Table from "components/Table";
import {
  DEFAULT_MEMBER_STATUS,
  EMAIL_VIEW_BY_OPTIONS,
  MYSQL_DATE_FORMAT,
  VIEW_INTERVAL_OPTIONS,
} from "constants/index";
import CustomSelect from "containers/Members/Select";
import GeneratePdf, { GeneratePdfRef } from "containers/Reports/components/GeneratePdf";
import { ReportTableOptions } from "containers/Reports/components/ReportTableOptions";
import { parseISO } from "date-fns";
import { useQueryParams } from "hooks/useQueryParams";
import { GroupForListing } from "models/group";
import { TTCAGraduationsReport } from "models/report";
import { TPagination } from "modules/api/type";
import { getGroups } from "modules/group/groupServices";
import { getTCAGraduationsReport } from "modules/report/reportService";
import moment from "moment";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { columnsForPdf, columnsForPrint, formatData, generateCsvData } from "./data";

const GraduationReport = () => {
  const [params, setParams, defaultParams, setObjParams] = useQueryParams({
    date_from: "",
    date_to: "",
    group_id: "",
    limit: 10,
    page: 1,
    // searchValue: "",
    sort_by: "",
    sort_order: "",
    status: DEFAULT_MEMBER_STATUS,
    view_by: EMAIL_VIEW_BY_OPTIONS[0].value,
    view_interval_options: "", //VIEW_INTERVAL_OPTIONS[0].value,
  });
  const [allGroups, setAllGroups] = useState<GroupForListing[]>([]);
  const [isFetching, setIsFetching] = useState(false);
  const [isFetchingAllData, setIsFetchingAllData] = useState(false);
  const [data, setData] = useState<TTCAGraduationsReport[]>([]);
  const [allData, setAllData] = useState<TTCAGraduationsReport[]>([]);
  const [pagination, setPagination] = useState<TPagination>({
    current_page: 0,
    data_from_offset: 0,
    data_to_offset: 0,
    per_page: PAGE_OPTIONS[0],
    total_pages: 0,
    total_records: 0,
  });
  const pdfRef = useRef<GeneratePdfRef>(null);
  const printRef = useRef<GeneratePdfRef>(null);

  useEffect(() => {
    //TODO: set limit to 0 once this is in place
    getGroups({ sort_by: "GROUP_TITLE" }).then((result) => {
      if (result.success) {
        setAllGroups(result.data);
      }
    });

    fetchReport(params);
  }, []);

  const fetchReport = useCallback(
    async (parameters) => {
      if (isFetching) return;
      setIsFetching(true);
      let result = await getTCAGraduationsReport(parameters);
      setIsFetching(false);
      if (result.success) {
        setData(formatData(result.data));
        setPagination(result.pagination);
        const tempParams = { ...parameters, limit: 0 };
        setIsFetchingAllData(true);
        let res = await getTCAGraduationsReport(tempParams);
        if (res.success) {
          setAllData(formatData(res.data));
        }
        setIsFetchingAllData(false);
      }
    },
    [
      setData,
      setPagination,
      isFetching,
      setIsFetching,
      isFetchingAllData,
      setIsFetchingAllData,
      setAllData,
    ]
  );

  const csvData = useMemo(() => {
    return generateCsvData(allData);
  }, [allData]);

  //#region event handlers
  const onChangeFilterHandler = (e) => {
    let { name, value } = e.target;
    setParams(name, value);
  };

  const handleStatusChange = (values) => {
    setParams("status", values.join(","));
  };

  const onSubmitFilter = () => {
    let temp = { ...params, page: 1 };
    setParams("page", 1);
    fetchReport(temp);
  };

  const onChangeDateHandler = (key, m) => {
    const date = moment(m).format(MYSQL_DATE_FORMAT);
    if (date === "Invalid Date") {
      setParams(key, "");
    } else {
      setParams(key, date);
    }
  };

  const handlePaginationOptionChange = useCallback(
    (event: SelectChangeEvent<any>) => {
      let temp = { ...params, limit: event.target.value, page: 1 };
      setParams("limit", event.target.value);
      fetchReport(temp);
    },
    [setParams]
  );

  const handlePageChange = useCallback(
    (page: number) => {
      let temp = { ...params, page };
      setParams("page", page);
      fetchReport(temp);
    },
    [setParams]
  );

  const onSortChange = (sortModel) => {
    const { field = "", sort = "" } = sortModel?.length ? sortModel[0] : {};
    let temp = {
      ...params,
      page: 1,
      sort_by: field?.toUpperCase(),
      sort_order: sort?.toUpperCase(),
    };
    setObjParams({
      page: 1,
      sort_by: field?.toUpperCase(),
      sort_order: sort?.toUpperCase(),
    });
    fetchReport(temp);
  };

  const onClickPdfHandler = () => {
    pdfRef?.current.exportContent();
  };

  const onClickPrintHandler = () => {
    printRef?.current.printContent();
  };
  //#endregion event handlers

  return (
    <>
      <Stack gap={1.25}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="start"
          mt="25px"
          flexWrap="wrap"
          gap="20px"
        >
          <Dropdown
            name="view_by"
            labelId="View By"
            label="View By"
            className=""
            value={params.view_by}
            defaultValue={defaultParams.view_by}
            onChange={onChangeFilterHandler}
          >
            {EMAIL_VIEW_BY_OPTIONS.map((item) => (
              <MenuItem key={item.value} value={item.value}>
                {item.label}
              </MenuItem>
            ))}
          </Dropdown>
          <CustomSelect defaultOptions={[DEFAULT_MEMBER_STATUS]} handler={handleStatusChange} />
          <Dropdown
            name="group_id"
            labelId="Groups"
            label="Groups"
            className=""
            value={params.group_id}
            defaultValue={defaultParams.members_status}
            onChange={onChangeFilterHandler}
          >
            {allGroups.map((item) => (
              <MenuItem key={item.group_id} value={item.group_id}>
                {item.group_title}
              </MenuItem>
            ))}
          </Dropdown>
          <Dropdown
            name="sort_by"
            label="Sort By"
            className=""
            value={params.sort_by}
            defaultValue={defaultParams.sort_by}
            onChange={onChangeFilterHandler}
          >
            <MenuItem value="COMPLETED_APPT_1">Completed Appt. #1</MenuItem>
            <MenuItem value="COMPLETED_APPT_2">Completed Appt. #2</MenuItem>
            <MenuItem value="COMPLETED_APPT_3">Completed Appt. #3</MenuItem>
            <MenuItem value="COMPLETED_APPT_4">Completed Appt. #4</MenuItem>
            <MenuItem value="COMPLETED_APPT_5">Completed Appt. #5</MenuItem>
            <MenuItem value="INSTRUCTOR_NAME">Instructor Name</MenuItem>
            <MenuItem value="NO_OF_MEMBERS">Number of Members</MenuItem>
            <MenuItem value="PERCENT_GRADUATED">Percent Graduated</MenuItem>
          </Dropdown>
        </Stack>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="start"
          flexWrap="wrap"
          gap="20px"
        >
          <Dropdown
            name="view_interval_options"
            labelId="Initial Appt. Date Range"
            label="Initial Appt. Date Range"
            className="base-dropdown"
            value={params.view_interval_options}
            defaultValue={defaultParams.view_interval_options}
            onChange={onChangeFilterHandler}
          >
            {VIEW_INTERVAL_OPTIONS.map((item) => (
              <MenuItem key={item.value} value={item.value}>
                {item.label}
              </MenuItem>
            ))}
          </Dropdown>
          {params.view_interval_options === "CUSTOM" && (
            <>
              <DatePicker
                label="Date From"
                className="base-dropdown"
                value={parseISO(params.date_from)}
                onChange={(m: any) => onChangeDateHandler("date_from", m)}
              />
              <DatePicker
                label="Date To"
                className="base-dropdown"
                value={parseISO(params.date_to)}
                onChange={(m: any) => onChangeDateHandler("date_to", m)}
              />
            </>
          )}
        </Stack>
        <Button
          label="Refresh Report"
          className="refresh-report-btn"
          color="primary-blue"
          onClick={onSubmitFilter}
        />
      </Stack>
      <ReportTableOptions
        showOptions={!!allData.length}
        title="Report Content"
        type="tca-graduations"
        onClickPdf={onClickPdfHandler}
        onClickPrint={onClickPrintHandler}
        csvData={csvData}
        isLoading={isFetchingAllData}
      />
      <Table
        rows={data}
        sortingMode="server"
        onSortModelChange={onSortChange}
        paginationMode="server"
        pagination
        page={pagination.current_page}
        rowCount={pagination.total_records}
        paginationProps={{
          ...pagination,
          onPageChange: handlePageChange,
          pageDropdownProps: {
            defaultValue: pagination.per_page,
            onChange: handlePaginationOptionChange,
            value: pagination.per_page,
          },
        }}
        loading={isFetching}
        slots={undefined}
        columns={[]}
        headerHeight={undefined}
        pageSize={undefined}
        rowsPerPageOptions={undefined}
        onPageChange={function (page: number): void {
          throw new Error("Function not implemented.");
        }}
      />
      <Box className="visually-hidden">
        <GeneratePdf
          ref={pdfRef}
          title="TCA Graduations"
          type="tca-graduations"
          tableData={{ columns: columnsForPdf, rows: allData }}
          className="compact-cells coach-approach-graduation-pdf"
        />
      </Box>

      <Box className="visually-hidden">
        <GeneratePdf
          ref={printRef}
          title="TCA Graduations"
          type="tca-graduations"
          tableData={{ columns: columnsForPrint, rows: allData }}
          className="compact-cells coach-approach-graduation-print"
        />
      </Box>
    </>
  );
};

export default GraduationReport;
